/**
 * GalleryGrid — 3-col grid of GalleryCards grouped by date (Phase 4).
 *
 * Per handoff §11:
 *  - группировка по датам
 *  - внутри даты newest first, слева направо, сверху вниз
 *  - lazy load при scroll к нижнему краю (IntersectionObserver)
 *
 * Props:
 *  - items       ([{ id, type, createdAt, ... }])
 *  - onItemClick (fn(item))
 *  - onLoadMore  (fn) — called when scroll sentinel becomes visible
 *  - hasMore     (bool) — render sentinel?
 */
import { useEffect, useRef } from 'react';
import GalleryCard from './GalleryCard';
import DateSeparator from './DateSeparator';

function groupByDate(items) {
  const map = new Map();
  for (const item of items) {
    const d = new Date(item.createdAt);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!map.has(key)) {
      map.set(key, { iso: item.createdAt, items: [] });
    }
    map.get(key).items.push(item);
  }
  // Map preserves insertion order — first occurrence wins → newest-first because
  // items arrive sorted by createdAt desc from backend.
  return Array.from(map.values());
}

export default function GalleryGrid({ items, onItemClick, onLoadMore, hasMore }) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore || typeof onLoadMore !== 'function') return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) onLoadMore();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, onLoadMore]);

  const groups = groupByDate(items);

  return (
    <div className="flex flex-col gap-3 pb-24" data-testid="gallery-grid">
      {groups.map((g) => (
        <div key={g.iso} className="flex flex-col gap-2 px-body-x">
          <DateSeparator isoDate={g.iso} />
          <div className="grid grid-cols-3 gap-3">
            {g.items.map((item) => (
              <GalleryCard
                key={`${item.type}-${item.id}`}
                item={item}
                onClick={onItemClick}
              />
            ))}
          </div>
        </div>
      ))}
      {hasMore && (
        <div
          ref={sentinelRef}
          data-testid="gallery-load-more-sentinel"
          className="h-12 w-full"
          aria-hidden
        />
      )}
    </div>
  );
}
